import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Box, Flex } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import Card from "../../../components/core/basic/Card";
import { TEAM_COLOR } from "../theme";

/** Past this, the wait is long enough that the user needs reassuring it is still going. */
const STILL_WORKING_AFTER_SECONDS = 45;

/** Counts from when the job started on the server, so reloading the page doesn't restart the clock. */
function useElapsedSeconds(startedAt: string | undefined) {
  const [mountedAt] = useState(() => Date.now());
  const from = startedAt ? Date.parse(startedAt) : mountedAt;
  const elapsed = () => Math.max(0, Math.floor((Date.now() - from) / 1000));
  const [seconds, setSeconds] = useState(elapsed);
  useEffect(() => {
    const tick = () => setSeconds(Math.max(0, Math.floor((Date.now() - from) / 1000)));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [from]);
  return seconds;
}

const formatElapsed = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

export function Spinner({ size }: { size: number }) {
  return (
    <span
      aria-hidden
      className={css({
        display: "inline-block",
        flexShrink: 0,
        borderRadius: "pill",
        borderStyle: "solid",
        animation: "spin",
        animationDuration: "0.9s",
      })}
      style={{
        width: size,
        height: size,
        borderWidth: Math.max(2, Math.round(size / 12)),
        borderColor: "#e4dcf1",
        borderTopColor: TEAM_COLOR,
      }}
    />
  );
}

function Elapsed({ startedAt }: { startedAt: string | undefined }) {
  const { t } = useTranslation(["user_activity"]);
  const seconds = useElapsedSeconds(startedAt);
  return (
    <span className={css({ color: "text.secondary", fontSize: "small", fontVariantNumeric: "tabular-nums" })}>
      {seconds >= STILL_WORKING_AFTER_SECONDS ?
        t(($) => $.user_activity["Still working ({elapsed}) — large accounts take longer."], {
          elapsed: formatElapsed(seconds),
        })
      : t(($) => $.user_activity["Working for {elapsed}"], { elapsed: formatElapsed(seconds) })}
    </span>
  );
}

/** First load: nothing to show yet, so say plainly that the wait is expected and how long it can be. */
export function GeneratingReport({ days, startedAt }: { days: number; startedAt: string | undefined }) {
  const { t } = useTranslation(["user_activity"]);

  return (
    <Flex direction="column" gap="medium" aria-busy>
      <Card
        css={{
          display: "flex",
          flexDirection: "column",
          gap: "small",
          alignItems: "center",
          paddingY: "xlarge",
          paddingX: "large",
          borderRadius: "standard",
          textAlign: "center",
        }}
      >
        <Spinner size={44} />
        <div role="status" aria-live="polite">
          <h2
            className={css({
              margin: 0,
              marginTop: "small",
              color: "text.heading",
              fontSize: "xlarge",
              fontWeight: "600",
            })}
          >
            {t(($) => $.user_activity["Building the report…"])}
          </h2>
          <p
            className={css({
              maxWidth: "560px",
              margin: 0,
              marginTop: "xsmall",
              color: "text.secondary",
              fontSize: "medium",
            })}
          >
            {t(
              ($) =>
                $.user_activity[
                  "We're gathering {days} days of activity for everyone in this organization. This can take a minute or two — keep this tab open and the report will appear here."
                ],
              { days },
            )}
          </p>
        </div>
        <Elapsed startedAt={startedAt} />
      </Card>
    </Flex>
  );
}

/** A report is already on screen and a new one is on its way (another period, or a refresh). */
export function UpdatingBanner({ days, startedAt }: { days: number; startedAt: string | undefined }) {
  const { t } = useTranslation(["user_activity"]);
  return (
    <Flex
      position="sticky"
      top="small"
      zIndex="sticky"
      gap="small"
      alignItems="center"
      flexWrap="wrap"
      border="1px solid #e4dcf1"
      borderRadius="standard"
      paddingY="small"
      paddingX="medium"
      fontSize="medium"
      boxShadow="0 4px 16px rgba(46, 0, 84, 0.08)"
      backgroundColor="white"
    >
      <Spinner size={18} />
      <span role="status" aria-live="polite" className={css({ color: "text.heading", fontWeight: "500" })}>
        {t(($) => $.user_activity["Updating the report for the last {days} days. This can take a minute or two."], {
          days,
        })}
      </span>
      <Box marginLeft="auto">
        <Elapsed startedAt={startedAt} />
      </Box>
    </Flex>
  );
}

/** The job failed. With a report already on screen this is a slim banner above it; without one, a card. */
export function JobFailedNotice({ onRetry, compact }: { onRetry: () => void; compact: boolean }) {
  const { t } = useTranslation(["user_activity"]);
  const retry = (
    <Button variant="secondary" onPress={onRetry}>
      {t(($) => $.user_activity["Try again"])}
    </Button>
  );
  if (compact) {
    return (
      <Flex
        gap="small"
        alignItems="center"
        flexWrap="wrap"
        border="1px solid #f3c7c7"
        borderRadius="standard"
        paddingY="small"
        paddingX="medium"
        fontSize="medium"
        backgroundColor="#fff6f6"
      >
        <span role="alert" className={css({ color: "text.heading", fontWeight: "500" })}>
          {t(($) => $.user_activity["The latest update didn't finish. You're looking at the previous report."])}
        </span>
        <Box marginLeft="auto">{retry}</Box>
      </Flex>
    );
  }
  return (
    <Card
      css={{
        display: "flex",
        flexDirection: "column",
        gap: "small",
        alignItems: "center",
        paddingY: "xlarge",
        paddingX: "large",
        borderRadius: "standard",
        textAlign: "center",
      }}
    >
      <div role="alert">
        <h2 className={css({ margin: 0, color: "text.heading", fontSize: "xlarge", fontWeight: "600" })}>
          {t(($) => $.user_activity["We couldn't build this report."])}
        </h2>
        <p className={css({ margin: 0, marginTop: "xsmall", color: "text.secondary", fontSize: "medium" })}>
          {t(($) => $.user_activity["Something went wrong while gathering the activity. Try again in a moment."])}
        </p>
      </div>
      {retry}
    </Card>
  );
}
